import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const InvoiceTemplate = ({ invoice, formatCurrency, formatDate }) => {
  const { t } = useLanguage();

  if (!invoice) return null;

  // Handle different invoice data structures
  const isDetailedInvoice = invoice.seller && invoice.buyer && invoice.lines;
  const isFormInvoice = invoice.items && !invoice.lines;

  return (
    <div className="bg-white shadow-lg rounded-lg p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('invoice')}</h1>
          <p className="text-gray-600">
            {t('invoiceNumber')}: <span className="font-mono font-semibold">
              {invoice.seriesNumber || invoice.invoiceNr || invoice.invoiceNumber}
            </span>
          </p>
        </div>
        <div className="text-right">
          {isDetailedInvoice && invoice.seller && (
            <div className="text-sm text-gray-600 mb-4">
              <div className="font-semibold">{invoice.seller.name}</div>
              <div>{invoice.seller.address}</div>
              {/* <div>{invoice.seller.city}</div> */}
              <div>{t('taxIdentifier')}: {invoice.seller.tax_identifier}</div>
              {invoice.seller.reg_com && <div>{t('regCom')}: {invoice.seller.reg_com}</div>}
            </div>
          )}
        </div>
      </div>

      {/* Invoice Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('billedTo')}</h3>
          <div className="text-gray-700">
            {isDetailedInvoice && invoice.buyer ? (
              <>
                <div className="font-semibold">{invoice.buyer.name}</div>
                {invoice.buyer.tax_identifier && (
                  <div>{t('taxIdentifier')}: {invoice.buyer.tax_identifier}</div>
                )}
                {invoice.buyer.reg_com && (
                  <div>{t('regCom')}: {invoice.buyer.reg_com}</div>
                )}
                <div>{invoice.buyer.address}</div>
                <div>{invoice.buyer.city}</div>
                <div>{invoice.buyer.country_code}</div>
              </>
            ) : (
              <>
                <div className="font-semibold">
                  {invoice.clientName || invoice.counterpartyName || invoice.buyerName}
                </div>
                {invoice.clientEmail && <div>{invoice.clientEmail}</div>}
                {invoice.clientAddress && <div>{invoice.clientAddress}</div>}
                
                {invoice.clientPhone && <div>{invoice.clientPhone}</div>}
                {invoice.clientTaxIdentifier && (
                  <div>{t('taxIdentifier')}: {invoice.clientTaxIdentifier}</div>
                )}
              </>
            )}
          </div>
        </div>
        
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('invoiceDetails')}</h3>
          <div className="text-gray-700 space-y-1">
            <div>
              <span className="font-medium">{t('issueDate')}:</span> {formatDate(invoice.issueDate || invoice.invoiceDate)}
            </div>
            {invoice.dueDate && (
              <div>
                <span className="font-medium">{t('dueDate')}:</span> {formatDate(invoice.dueDate)}
              </div>
            )}
            <div>
              <span className="font-medium">{t('status')}:</span>
              <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                invoice.eInvoiceStatus === 'SENT' || invoice.status === 'SENT' || invoice.status === 'sent'
                  ? 'bg-green-100 text-green-800'
                  : invoice.eInvoiceStatus === 'PENDING' || invoice.status === 'PENDING'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {invoice.eInvoiceStatus || invoice.status || t('draft')}
              </span>
            </div>
            {invoice.currency && (
              <div>
                <span className="font-medium">{t('currency')}:</span> {invoice.currency}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('items')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-900">
                  {t('description')}
                </th>
                <th className="border border-gray-300 px-4 py-2 text-center text-sm font-medium text-gray-900">
                  {t('quantity')}
                </th>
                <th className="border border-gray-300 px-4 py-2 text-right text-sm font-medium text-gray-900">
                  {t('unitPrice')}
                </th>
                <th className="border border-gray-300 px-4 py-2 text-right text-sm font-medium text-gray-900">
                  VAT %
                </th>
                <th className="border border-gray-300 px-4 py-2 text-right text-sm font-medium text-gray-900">
                  {t('total')}
                </th>
              </tr>
            </thead>
            <tbody>
              {isDetailedInvoice && invoice.lines ? 
                invoice.lines.map((item, index) => (
                  <tr key={index}>
                    <td className="border border-gray-300 px-4 py-2 text-sm">
                      <div className="font-medium">{item.name}</div>
                      {item.identification && (
                        <div className="text-gray-500 text-xs">ID: {item.identification}</div>
                      )}
                      {item.type && (
                        <div className="text-gray-500 text-xs">Type: {item.type}</div>
                      )}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center text-sm">
                      {item.quantity} {item.um && `(${item.um})`}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right text-sm">
                      {formatCurrency(item.unitPrice)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right text-sm">
                      {item.vatRate}%
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right text-sm font-medium">
                      {formatCurrency(item.lineTotal)}
                    </td>
                  </tr>
                )) :
                isFormInvoice && invoice.items ? 
                invoice.items.map((item, index) => (
                  <tr key={index}>
                    <td className="border border-gray-300 px-4 py-2 text-sm">
                      {item.description}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-center text-sm">
                      {item.quantity}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right text-sm">
                      {formatCurrency(item.price)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right text-sm">
                      {invoice.vatRate}%
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right text-sm font-medium">
                      {formatCurrency(item.quantity * item.price)}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="5" className="border border-gray-300 px-4 py-2 text-center text-sm text-gray-500">
                      {t('noItemsFound')}
                    </td>
                  </tr>
                )
              }
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals */}
      <div className="flex justify-end mb-8">
        <div className="w-64">
          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="text-gray-600">{t('subtotal')}:</span>
            <span className="font-medium">
              {formatCurrency(invoice.amount || invoice.subtotal || 0)}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="text-gray-600">VAT:</span>
            <span className="font-medium">
              {formatCurrency(invoice.taxAmount || invoice.vat || 0)}
            </span>
          </div>
          <div className="flex justify-between py-3 border-b-2 border-gray-300">
            <span className="text-lg font-semibold text-gray-900">{t('totalAmount')}:</span>
            <span className="text-lg font-bold text-gray-900">
              {formatCurrency(invoice.totalAmount || invoice.total || 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {invoice.notes && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('notes')}</h3>
          <p className="text-gray-700 text-sm">{invoice.notes}</p>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-gray-200 pt-6 text-center text-sm text-gray-500">
        <p>{t('footer')}</p>
        <p className="mt-1">{t('footerContact')}</p>
      </div>
    </div>
  );
};

export default InvoiceTemplate;
